import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { deriveHours } from "@/lib/budget-hours";
import { evaluateHoursChange, evaluateDescriptionChange, type LineGuardContext } from "@/lib/budget-revision";
import type { RevisionDraftLine } from "@/lib/budget-revision-draft";

export type { RevisionDraftLine };

const UNITS_INTERNAL = "internal";

export function BudgetRevisionRow({
  draft,
  guard,
  originalHours,
  originalDescription,
  onChange,
  onToggleDelete,
}: {
  draft: RevisionDraftLine;
  guard: LineGuardContext;
  originalHours: number | null;
  originalDescription: string | null;
  onChange: (patch: Partial<RevisionDraftLine>) => void;
  onToggleDelete: () => void;
}) {
  const isExternal = draft.cost_type === "external";
  const hoursChanged = !draft.isNew && (originalHours ?? 0) !== (draft.quantity_hours ?? 0);
  const verdict = hoursChanged ? evaluateHoursChange(draft.quantity_hours ?? 0, guard) : "ok";
  const descriptionChanged = !draft.isNew && (originalDescription ?? "") !== draft.description;
  const descriptionWarning =
    descriptionChanged && evaluateDescriptionChange(guard) === "needs_confirmation";

  const num = (n: number | null | undefined) => (n ? String(n) : "");

  return (
    <tr
      className={cn(
        "border-b border-border/40 align-top",
        draft.deleted && "opacity-50 line-through",
        draft.isNew && "bg-emerald-50/40",
      )}
    >
      <td className="px-4 py-1.5 pl-8">
        <div className="flex items-center gap-2">
          <Input
            value={draft.description}
            className="h-8"
            disabled={draft.deleted}
            onChange={(e) => onChange({ description: e.target.value })}
          />
          <Select
            value={draft.cost_type}
            onValueChange={(v) => {
              const next = v as "internal" | "external";
              onChange({
                cost_type: next,
                quantity_hours: deriveHours(next, "t", draft.quantity),
              });
            }}
          >
            <SelectTrigger className="h-8 w-24 px-2 text-xs" disabled={draft.deleted}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Intern</SelectItem>
              <SelectItem value="external">Kostnad</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {verdict === "needs_confirmation" && (
          <p className="mt-1 ml-1 text-[10.5px] text-amber-700">
            Under loggede/fullførte timer ({Math.max(guard.loggedHours, guard.completedApprovedHours)}t) — krever bekreftelse.
          </p>
        )}
        {verdict === "blocked_month_lock" && (
          <p className="mt-1 ml-1 text-[10.5px] text-destructive">
            Fullført tildeling i en låst måned — kan ikke reduseres.
          </p>
        )}
        {descriptionWarning && (
          <p className="mt-1 ml-1 text-[10.5px] text-amber-700">
            Endret navn på en linje med loggede timer — kan svekke koblingen til timeføringene.
          </p>
        )}
      </td>
      <td className="px-3 py-1.5 text-right">
        <Input
          type="number"
          step="0.25"
          className="h-8 text-right tabular-nums w-20"
          value={draft.cost_type === UNITS_INTERNAL ? num(draft.quantity_hours) : num(draft.quantity)}
          disabled={draft.deleted}
          onChange={(e) => {
            const v = e.target.value === "" ? null : Number(e.target.value);
            if (draft.cost_type === UNITS_INTERNAL) {
              onChange({ quantity: v, quantity_hours: v });
            } else {
              onChange({ quantity: v });
            }
          }}
        />
      </td>
      <td className="px-3 py-1.5 text-right">
        <Input
          type="number"
          className="h-8 text-right tabular-nums w-28"
          value={num(draft.amount)}
          disabled={draft.deleted}
          onChange={(e) => onChange({ amount: e.target.value === "" ? 0 : Number(e.target.value) })}
        />
      </td>
      <td className="px-4 py-1.5 text-right">
        {isExternal ? (
          <Input
            type="number"
            className="h-8 text-right tabular-nums w-24"
            value={num(draft.estimated_cost)}
            disabled={draft.deleted}
            onChange={(e) =>
              onChange({ estimated_cost: e.target.value === "" ? 0 : Number(e.target.value) })
            }
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-7 w-7 text-muted-foreground",
            draft.deleted ? "hover:text-primary" : "hover:text-destructive",
          )}
          onClick={onToggleDelete}
          title={draft.deleted ? "Angre sletting" : "Fjern linje"}
        >
          <SrIcon name={draft.deleted ? "rotate-ccw" : "trash2"} className="w-3.5 h-3.5" />
        </Button>
      </td>
    </tr>
  );
}
